import { Link, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ActivityCard } from '../components/executive-protection/ActivityCard';
import { cn } from '../utils/cn';
import type { ExecutiveProtectionContextType } from '../layouts/ExecutiveProtectionLayout';

export default function EPDashboard() {
  const { todaysActivities, geofenceAlerts, isLoading } = useOutletContext<ExecutiveProtectionContextType>();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Today's Activities */}
      <Card>
        <CardHeader>
          <CardTitle>Today&apos;s Activities</CardTitle>
          <CardDescription>
            Current and upcoming protection activities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading.todaysActivities ? (
            <div className="text-center py-8 text-secondary-500">Loading activities...</div>
          ) : todaysActivities.length === 0 ? (
            <div className="text-center py-8 text-secondary-500">No activities scheduled for today</div>
          ) : (
            <>
              {todaysActivities.slice(0, 3).map((activity) => (
                <ActivityCard key={activity.id} activity={activity} />
              ))}
            </>
          )}
          <Link to="/security/executive-protection/activities" className="mt-2 block">
            <Button variant="outline" className="w-full">
              View All Activities
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Recent Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Alerts</CardTitle>
          <CardDescription>
            Geofence and location alerts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading.alerts ? (
            <div className="text-center py-8 text-secondary-500">Loading alerts...</div>
          ) : geofenceAlerts.length === 0 ? (
            <div className="text-center py-8 text-secondary-500">No alerts found</div>
          ) : (
            <>
              {geofenceAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    'p-4 rounded-lg border-l-4',
                    alert.severity === 'critical' && 'border-danger-500 bg-danger-50 dark:bg-danger-900/20',
                    alert.severity === 'warning' && 'border-warning-500 bg-warning-50 dark:bg-warning-900/20',
                    alert.severity === 'info' && 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-secondary-900 dark:text-white">
                      {alert.geofenceName}
                    </h4>
                    <span className="text-xs text-secondary-500 dark:text-secondary-400">
                      {new Date(alert.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm text-secondary-700 dark:text-secondary-300">
                    {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)} - {alert.message}
                  </p>
                </div>
              ))}
              <Link to="/security/executive-protection/live-tracking">
                <Button variant="outline" className="w-full">
                  View All Alerts
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
